/**
 * @class Access contains a set of standard methods for getting and setting data for any database
 * @param {object} ref contains all the methods for interacting with the database
 * @param {object} firebase firebase database object
 */
export default class Access {

  constructor( ref, firebase ) {
    this._ref = ref
    this._firebase = firebase
  }

  /**
   * @function _refFor returns the ref for a child or the current ref
   * @param {string} path null for current ref or child string for a child
   */
  _refFor( path ) {
    return path == null || path === '' ? this._ref : this._ref.child( path )
  }

  /**
   * @function child returns an access object for the child
   * @param {string} path the path for the child
   */
  child( path ) {
    return new Access( this._refFor( path ), this._firebase )
  }

  /**
   * @function orderByChild orders results by the value of a specified child key or nested child path. 
   * @param {string} path the path to order
   * @param {string} keyPath the key path to order by
   */
  orderByChild( path, keyPath ) { 
    return this._refFor( path ).orderByChild( keyPath )
  }

  /**
   * @function startAt defines the start point for a query
   * @param {string} path the path to start at
   * @param {string} value the value to start at
   */

  startAt( path, value ) { 
    return this._refFor( path ).startAt( value )
  }

  /**
   * @function on starts an on listener 
   * @param {string} path the path to listen on
   * @param {function} onComplete 
   * @param {function} onCancelled 
   * @param {object} cxt 
   */
  on( path, onComplete, onCancelled, cxt ) {
    return this._refFor( path ).on( 'value', onComplete, onCancelled, cxt )
  }

  /**
   * @function off starts an off listener
   * @param {string} path the path to listen on
   * @param {function} callback 
   * @param {object} cxt 
   */
  off( path, callback, cxt ) {
    return this._refFor( path ).off( 'value', callback, cxt )
  }

  /**
   * @function onAdd starts a listener for when a child is added
   * @param {string} path the path to listen on
   * @param {function} onComplete 
   * @param {function} onCancelled 
   * @param {object} cxt 
   */
  onAdd( path, onComplete, onCancelled, cxt ) {
    return this._refFor( path ).on( 'child_added', onComplete, onCancelled, cxt )
  }

  /**
   * @function offAdd starts a listener for when a child is added
   * @param {string} path the path to listen on
   * @param {function} callback 
   * @param {object} cxt 
   */
  offAdd( path, callback, cxt ) {
    return this._refFor( path ).off( 'child_added', callback, cxt )
  }

  /**
   * @function onRemove start a listener for when a child is removed
   * @param {string} path the path to listen on
   * @param {function} onComplete 
   * @param {function} onCancelled 
   * @param {object} cxt 
   */
  onRemove( path, onComplete, onCancelled, cxt ) {
    return this._refFor( path ).on( 'child_removed', onComplete, onCancelled, cxt )
  }

  /**
   * @function offRemove start a listener for when a child is removed
   * @param {string} path the path to listen on
   * @param {function} callback 
   * @param {object} cxt 
   */
  offRemove( path, callback, cxt ) {
    return this._refFor( path ).off( 'child_removed', callback, cxt )
  }

  /**
   * @function onChange start a listener for when a child is changed
   * @param {string} path the path to listen on
   * @param {function} onComplete 
   * @param {function} onCancelled 
   * @param {object} cxt 
   */
  onChange( path, onComplete, onCancelled, cxt ) {
    return this._refFor( path ).on( 'child_changed', onComplete, onCancelled, cxt )
  }

  /**
   * @function offChange start a listener for when a child is changed
   * @param {string} path the path to listen on
   * @param {function} callback 
   * @param {object} cxt 
   */
  offChange( path, callback, cxt ) {
    return this._refFor( path ).off( 'child_changed', callback, cxt )
  }

  /**
   * @function once make a one time query to the database for information
   * @param {string} path the path to listen on
   * @param {function} onComplete 
   * @param {function} onError 
   */
  once( path, onComplete, onError ) {
    return this._refFor( path ).once( 'value', onComplete, onError )
  }

  /**
   * @function set sets the value at the given path
   * @param {string} path the path to set the value on
   * @param {any} value the value to set
   * @param {function} onComplete 
   */
  set( path, value, onComplete ) {
    return this._refFor( path ).set( value, onComplete )
  }

  /**
   * @function push pushes the value at the given path
   * @param {string} path the path to push the value to
   * @param {any} value the value to be pushed
   * @param {function} onComplete 
   */
  push( path, value, onComplete ) {
    return this._refFor( path ).push( value, onComplete )
  }

  /**
   * @function update updates the value at the given path
   * @param {string} path the path to update the data at
   * @param {any} value the value to be updated
   * @param {function} onComplete 
   */
  update( path, value, onComplete ) {
    return this._refFor( path ).update( value, onComplete )
  }

  /**
   * @function remove the data at the given path
   * @param {string} path the path to remove the data at
   * @param {function} onComplete 
   */
  remove( path, onComplete ) {
    return this._refFor( path ).remove( onComplete )
  }

  /**
   * @function timestampValue returns the timestamp of the server
   */
  timestampValue() {
    return this._firebase.database.ServerValue.TIMESTAMP
  }
}
